import React, { useContext, useEffect } from 'react';
import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';

import {
  BrowserProps,
  MakePageResponseType,
  MakePageV3ResponseType,
  TemplateType,
} from '../../../types';

import { fetchTemplates, getAiPage, getMakePage } from '../../../lib/API';
import { CMSDimensionType, BaseAppType } from '../../../constants/common';
import useLazyload from 'src/hooks/useLazyload';
import { AnalyticsUtils } from '@invideoio/web-shared/utils/analytics/AnalyticsUtils';
import { registerAndSendPageViewEvent } from '@invideoio/web-shared/utils/analytics/Register';
import { Referrer } from '@invideoio/web-shared/utils/utils.constant';
import { PageType } from '@invideoio/web-shared/types/Analytics';
import {
  attachTemplates,
  extractTemplateDimensions,
  extractTemplateIds,
} from '@utils/common.utils';
import MakeSEO from '@components/common/SEO/MakeSEO';
import { DispatchContext } from '@store/store';
import { setEditorParams, setRedirectionLink } from '@store/actions';
import useTranslation from 'next-translate/useTranslation';
import MakePageContainerV3 from '@layouts/MakePageContainerV3';

interface AiProps extends BrowserProps {
  aiPageState: MakePageResponseType & MakePageV3ResponseType;
}

const Ai = (props: AiProps) => {
  const { aiPageState } = props;
  aiPageState.BaseAppType = BaseAppType.AI;
  useLazyload();

  const router = useRouter();
  const dispatch = useContext(DispatchContext);

  const { lang } = useTranslation('make');

  const url_path =
    router.locale === 'en'
      ? router.asPath
      : `/${router.locale}${router.asPath}`;

  useEffect(() => {
    AnalyticsUtils.setReferrer(Referrer.WEBSITE);
    registerAndSendPageViewEvent(PageType.AI, {
      location: window.location.href,
    });

    if (aiPageState.heroSection?.CTAUrl) {
      dispatch(setRedirectionLink(aiPageState.heroSection?.CTAUrl));
    }

    if (aiPageState.ExtraData) {
      const { searchTerm, tabType, categoryTray } = aiPageState.ExtraData;

      dispatch(setEditorParams({ searchTerm, tabType, categoryTray }));
    }
  }, []);

  return (
    <React.Fragment>
      <MakeSEO
        seo={aiPageState.SEO}
        url={url_path}
        FAQItems={aiPageState.FaqItems}
        HowToSchema={aiPageState.HowToSchema}
        locale={lang}
      />

      {aiPageState && <MakePageContainerV3 makePageState={aiPageState} />}
    </React.Fragment>
  );
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  let aiPageState = null;
  try {
    const slug = ctx.query.id as string;

    const appName = process.env.NEXT_PUBLIC_APP_NAME;

    if (appName === 'invideo') {
      try {
        aiPageState = await getAiPage(slug, ctx.locale);
      } catch (error) {
        console.log(`Unable to fetch ai post`);
      }
    }

    if (!aiPageState) {
      return { notFound: true };
    }

    if (
      aiPageState &&
      aiPageState.templateSlide_1 &&
      aiPageState.templateSlide_2 &&
      aiPageState.templateSlide_3 &&
      aiPageState.templateSlide_4
    ) {
      const ids = extractTemplateIds(
        aiPageState.templateSlide_1,
        aiPageState.templateSlide_2,
        aiPageState.templateSlide_3,
        aiPageState.templateSlide_4,
      );
      const dimensions: CMSDimensionType[] = extractTemplateDimensions(
        aiPageState.templateSlide_1,
        aiPageState.templateSlide_2,
        aiPageState.templateSlide_3,
        aiPageState.templateSlide_4,
      );
      const templates: TemplateType[] = await fetchTemplates(ids, dimensions);

      if (ids.length !== templates.length) {
        throw `Could not fetch few images from ${ids} got ${templates}`;
      }
      aiPageState = {
        ...aiPageState,
        ...attachTemplates({
          templateSlide1: aiPageState.templateSlide_1,
          templateSlide2: aiPageState.templateSlide_2,
          templateSlide3: aiPageState.templateSlide_3,
          templateSlide4: aiPageState.templateSlide_4,
          images: templates,
        }),
      };
    }

    //mini video wall slide
    if (aiPageState && aiPageState.videoWallSlide) {
      const ids = extractTemplateIds(aiPageState.videoWallSlide);
      const dimensions: CMSDimensionType[] = extractTemplateDimensions(
        aiPageState.videoWallSlide,
      );
      const templates: TemplateType[] = await fetchTemplates(ids, dimensions);
      if (ids.length !== templates.length) {
        throw `Could not fetch few images from ${ids} got ${templates}`;
      }
      const attachedTemplates = attachTemplates({
        templateSlide1: aiPageState.videoWallSlide,
        images: templates,
      });

      aiPageState = {
        ...aiPageState,
        videoWallSlide: attachedTemplates.templateSlide_1,
      };
    }
  } catch (error) {
    console.log(error);
    return { notFound: true };
  }
  return { props: { aiPageState } };
}

export default Ai;
